import { selectActiveTeam } from 'features/match/matchSlice';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Team from 'interfaces/team';

import { inputSliceActions, selectActiveSelectId, selectSelects } from 'features/input/inputSlice';

import { Copy, Title } from 'styled/components/Typography';

import StyledPlayerList, { StyledPlayerListItem } from './StyledPlayerList';
import { PlayerListItemProps } from './PlayerListTypes';
import elementId from '../../constants/elementId';
import { Person } from '../../interfaces/person';
import Flag from '../Flag/Flag';

const PlayerListItem: React.FC<PlayerListItemProps> = ({ player, index, onClick, isActive, disabled }) => {
  const { id, shirtNumber, name, country } = player;

  return (
    <StyledPlayerListItem
      key={id}
      greyBackground={index % 2 === 0}
      onClick={() => onClick(player)}
      isActive={isActive}
      disabled={!!disabled}
    >
      <Copy className="shirt-number">{shirtNumber}</Copy>
      {country && <Flag countryName={country.name} />}
      {!country && <span>&nbsp;</span>}
      <Copy className="name">{name}</Copy>
    </StyledPlayerListItem>
  );
};

const PlayerList: React.FC = () => {
  const dispatch = useDispatch();

  const team: Team | null = useSelector(selectActiveTeam);
  const selects = useSelector(selectSelects);

  if (!team) return null;

  const { persons: players } = team;
  const playerCurrentlyOnTheField = players.filter((player) => player.currentlyOnTheField);
  const playerCurrentlyOnTheBench = players.filter((player) => {
    return player.startsOnTheBench && !player.currentlyOnTheField && !player.entersFieldMinute;
  });

  const activeSelectId = useSelector(selectActiveSelectId);
  const playerOnNotSelected = elementId.playerOn === activeSelectId;
  const playerOffNotSelected = elementId.playerOff === activeSelectId;

  const selectPlayerOff = (player: Person) => {
    dispatch(inputSliceActions.setSelectPlayerOff(player.personId));
  };

  const selectPlayerOn = (player: Person) => {
    dispatch(inputSliceActions.setSelectPlayerOn(player.personId));
    if (!selects.playerOff.value) {
      dispatch(inputSliceActions.setActiveSelect(elementId.playerOff));
    }
  };

  return (
    <StyledPlayerList>
      <div className="section first">
        <Title>{team.name}</Title>
        <div className="list">
          {playerCurrentlyOnTheField.map((player, index) => (
            <PlayerListItem
              key={player.id!.toString() + index}
              player={player}
              index={index}
              onClick={selectPlayerOff}
              isActive={player.personId === selects.playerOff.value}
              disabled={!playerOffNotSelected}
            />
          ))}
        </div>
      </div>
      <div className="section second">
        <Title>
          <FormattedMessage id="playerList.substitutes" />
        </Title>
        <div className="list">
          {playerCurrentlyOnTheBench.map((player, index) => (
            <PlayerListItem
              key={player.id!.toString() + index}
              player={player}
              index={index}
              onClick={selectPlayerOn}
              isActive={player.personId === selects.playerOn.value}
              disabled={!playerOnNotSelected}
            />
          ))}
        </div>
      </div>
    </StyledPlayerList>
  );
};

export default PlayerList;
